import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Menu, X, ArrowRight, LayoutGrid, Eye, ShieldCheck, Sparkles,
  ArrowDownToLine, ArrowUpFromLine, Wallet, CalendarClock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import {
  BrowserFrame, DashboardMock, PaymentsCollectionsMock, ChecksMock, CashPosCardMock,
} from "@/components/landing/ProductMockups";

const NAVY = "#0A1128";
const BLUE = "#2563EB";

const NAV_ITEMS = [
  { id: "urun", label: "Ürün", tid: "landing-nav-urun" },
  { id: "ozellikler", label: "Özellikler", tid: "landing-nav-ozellikler" },
  { id: "nasil-calisir", label: "Nasıl Çalışır?", tid: "landing-nav-nasil-calisir" },
  { id: "hakkimizda", label: "Hakkımızda", tid: "landing-nav-hakkimizda" },
  { id: "iletisim", label: "İletişim", tid: "landing-nav-iletisim" },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

function LandingHeader() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); scrollTo(id); };
  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/80" data-testid="landing-header">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <button type="button" onClick={() => go("top")} aria-label="TEMHA" data-testid="landing-logo-button">
          <Wordmark size="h-7" />
        </button>
        <nav className="hidden lg:flex items-center gap-7" aria-label={t("Menü")}>
          {NAV_ITEMS.map((n) => (
            <button key={n.id} type="button" onClick={() => go(n.id)} data-testid={n.tid}
              className="text-sm text-slate-600 hover:text-[#0A1128] font-medium transition-colors duration-150">
              {t(n.label)}
            </button>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200">
            <Link to="/login" data-testid="landing-login-button">{t("Giriş Yap")}</Link>
          </Button>
        </div>
        <button type="button" className="lg:hidden p-2 text-slate-700" aria-label={t("Menü")}
          aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="landing-hamburger">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 space-y-1" data-testid="landing-mobile-menu">
          {NAV_ITEMS.map((n) => (
            <button key={n.id} type="button" onClick={() => go(n.id)} data-testid={`${n.tid}-mobile`}
              className="block w-full text-left px-2 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md">
              {t(n.label)}
            </button>
          ))}
          <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
            <LanguageToggle />
            <Button asChild className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              <Link to="/login" data-testid="landing-login-button-mobile">{t("Giriş Yap")}</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

function SectionHeading({ kicker, title, text, center = true, dark = false }) {
  const { t } = useLang();
  return (
    <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {kicker && (
        <p className={`text-xs font-semibold tracking-[0.18em] uppercase ${dark ? "text-blue-400" : "text-blue-600"}`}>
          {t(kicker)}
        </p>
      )}
      <h2 className={`mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight ${dark ? "text-white" : "text-[#0A1128]"}`}>
        {t(title)}
      </h2>
      {text && <p className={`mt-4 text-base leading-relaxed ${dark ? "text-slate-400" : "text-slate-500"}`}>{t(text)}</p>}
    </div>
  );
}

function ValueCards() {
  const { t } = useLang();
  const cards = [
    { icon: LayoutGrid, title: "Tek Merkez", text: "Finansal hareketlerinizi farklı tablolar ve araçlar arasında dağılmadan tek platformda takip edin.", tid: "value-card-tek-merkez" },
    { icon: Eye, title: "Bugünü Görün", text: "Kasadan POS'a, çeklerden tahsilatlara kadar günlük para hareketlerinizi daha kolay takip edin.", tid: "value-card-bugunu-gorun" },
    { icon: CalendarClock, title: "Yarını Planlayın", text: "Önünüzdeki ödeme ve tahsilatları görünür hale getirerek nakit akışınızı daha kolay planlayın.", tid: "value-card-yarini-planlayin" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-6">
      {cards.map((c) => (
        <div key={c.tid} data-testid={c.tid}
          className="border border-slate-200 bg-white rounded-lg p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-[transform,box-shadow] duration-300">
          <div className="h-10 w-10 rounded-md bg-blue-600/10 flex items-center justify-center">
            <c.icon className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="mt-5 font-display text-lg font-semibold text-[#0A1128]">{t(c.title)}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t(c.text)}</p>
        </div>
      ))}
    </div>
  );
}

function FeatureGroups() {
  const { t } = useLang();
  const groups = [
    { title: "Para girişlerinizi takip edin.", desc: "İşletmenize gelecek para hareketlerini tek merkezden takip edin.",
      modules: ["Alınan Çekler", "Diğer Tahsilatlar", "DBS", "POS Hareketleri"], accent: "text-green-700 bg-green-50 border-green-200", tid: "feature-group-giris" },
    { title: "Ödemelerinizi önceden görün.", desc: "Planlanan ve gerçekleşen ödemelerinizi düzenli şekilde takip edin.",
      modules: ["Kendi Çeklerimiz", "Bankaya Verilen Çekler", "Diğer Ödemeler", "Kredi Kartları", "Abonelikler"], accent: "text-red-700 bg-red-50 border-red-200", tid: "feature-group-cikis" },
    { title: "Mevcut para durumunuzu takip edin.", desc: "Finansal hareketlerinizi merkezi bir görünüm üzerinden takip edin.",
      modules: ["Kasa", "POS Hareketleri", "Kredi Kartları", "Panel"], accent: "text-blue-700 bg-blue-50 border-blue-200", tid: "feature-group-nakit" },
    { title: "Çeklerinizi düzenli şekilde yönetin.", desc: "Çek hareketlerinizi ayrı ayrı takip etmek yerine TEMHA üzerinden merkezi olarak yönetin.",
      modules: ["Alınan Çekler", "Bankaya Verilen Çekler", "Cirolanan Çekler", "Kendi Çeklerimiz"], accent: "text-indigo-700 bg-indigo-50 border-indigo-200", tid: "feature-group-cek" },
    { title: "Yönetim ve Veri", desc: "Kullanıcı erişimlerini ve veri yönetimini merkezi olarak kontrol edin.",
      modules: ["Kullanıcılar", "Yedekleme"], accent: "text-slate-700 bg-slate-100 border-slate-200", tid: "feature-group-yonetim" },
  ];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {groups.map((g) => (
        <div key={g.tid} data-testid={g.tid}
          className="border border-slate-200 bg-white rounded-lg p-7 hover:shadow-lg hover:shadow-slate-900/5 transition-shadow duration-300">
          <h3 className="font-display text-lg font-semibold text-[#0A1128]">{t(g.title)}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t(g.desc)}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {g.modules.map((m, i) => (
              <span key={`${m}-${i}`} className={`text-xs font-medium px-2.5 py-1 rounded-md border ${g.accent}`}>{t(m)}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Showcase({ flip = false, title, text, children, testid }) {
  const { t } = useLang();
  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center`} data-testid={testid}>
      <div className={flip ? "lg:order-2" : ""}>
        <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0A1128]">{t(title)}</h3>
        <p className="mt-3 text-base leading-relaxed text-slate-500">{t(text)}</p>
      </div>
      <div className={flip ? "lg:order-1" : ""}>{children}</div>
    </div>
  );
}

function MoneyFlow() {
  const { t } = useLang();
  const inflows = ["Alınan Çekler", "Tahsilatlar", "DBS", "POS"];
  const outflows = ["Çekler", "Ödemeler", "Kredi Kartları", "Abonelikler"];
  return (
    <div className="grid lg:grid-cols-3 gap-6 items-stretch">
      <div className="border border-green-200 bg-green-50/60 rounded-lg p-6" data-testid="flow-inflows">
        <div className="flex items-center gap-2 text-green-700">
          <ArrowDownToLine className="h-5 w-5" />
          <p className="text-sm font-semibold tracking-wide">{t("PARA GİRİŞLERİ")}</p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {inflows.map((m) => (
            <span key={m} className="text-xs font-medium px-2.5 py-1 rounded-md bg-white border border-green-200 text-green-800">{t(m)}</span>
          ))}
        </div>
      </div>
      <div className="rounded-lg p-6 flex flex-col items-center justify-center text-center text-white" style={{ background: NAVY }} data-testid="flow-center">
        <Wallet className="h-7 w-7 text-blue-400" />
        <p className="mt-3 text-sm font-semibold tracking-wide">{t("KASA / FİNANSAL GÖRÜNÜM")}</p>
        <p className="mt-2 text-xs leading-relaxed text-slate-400">{t("Tüm hareketler tek merkezde birleşir.")}</p>
      </div>
      <div className="border border-red-200 bg-red-50/60 rounded-lg p-6" data-testid="flow-outflows">
        <div className="flex items-center gap-2 text-red-700">
          <ArrowUpFromLine className="h-5 w-5" />
          <p className="text-sm font-semibold tracking-wide">{t("PARA ÇIKIŞLARI")}</p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {outflows.map((m) => (
            <span key={m} className="text-xs font-medium px-2.5 py-1 rounded-md bg-white border border-red-200 text-red-800">{t(m)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const { t } = useLang();
  const steps = [
    { no: "01", title: "Kayıtlarınızı yönetin", text: "Finansal hareketlerinizi TEMHA'da düzenli şekilde kaydedin.", tid: "step-1" },
    { no: "02", title: "Para akışınızı takip edin", text: "Ödemeleri, tahsilatları, çekleri, kasa, POS ve kart hareketlerini tek yerden takip edin.", tid: "step-2" },
    { no: "03", title: "Yarını planlayın", text: "Yaklaşan finansal hareketlerinizi görünür hale getirerek nakit akışınızı daha kolay planlayın.", tid: "step-3" },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {steps.map((s) => (
        <div key={s.no} data-testid={s.tid}
          className="relative border border-slate-200 bg-white rounded-lg p-7 overflow-hidden">
          <span className="absolute -top-3 right-4 font-display text-7xl font-bold text-slate-100 select-none" aria-hidden="true">{s.no}</span>
          <p className="text-xs font-bold tracking-[0.2em] text-blue-600">{s.no}</p>
          <h3 className="mt-3 font-display text-lg font-semibold text-[#0A1128]">{t(s.title)}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t(s.text)}</p>
        </div>
      ))}
    </div>
  );
}

function WhyTemha() {
  const { t } = useLang();
  const items = [
    { icon: LayoutGrid, title: "Tek Platform", text: "Finansal hareketlerinizi tek yerde takip edin.", tid: "why-tek-platform" },
    { icon: Eye, title: "Görünürlük", text: "Günlük ve yaklaşan para hareketlerinizi daha kolay görün.", tid: "why-gorunurluk" },
    { icon: ShieldCheck, title: "Kontrol", text: "Ödeme ve tahsilat süreçlerinizi düzenli şekilde yönetin.", tid: "why-kontrol" },
    { icon: Sparkles, title: "Sadelik", text: "Finansal hareketlerinizi karmaşık tablolar yerine anlaşılır bir arayüzde takip edin.", tid: "why-sadelik" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {items.map((i) => (
        <div key={i.tid} data-testid={i.tid} className="rounded-lg border border-white/10 bg-white/[0.04] p-6">
          <i.icon className="h-5 w-5 text-blue-400" />
          <h3 className="mt-4 font-display text-base font-semibold text-white">{t(i.title)}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(i.text)}</p>
        </div>
      ))}
    </div>
  );
}

function LandingFooter() {
  const { t } = useLang();
  const links = [
    { label: "Ana Sayfa", id: "top", tid: "footer-link-home" },
    { label: "Özellikler", id: "ozellikler", tid: "footer-link-features" },
    { label: "Nasıl Çalışır?", id: "nasil-calisir", tid: "footer-link-how" },
  ];
  const legal = [
    { label: "Gizlilik", tid: "footer-legal-privacy" },
    { label: "KVKK", tid: "footer-legal-kvkk" },
    { label: "Kullanım Koşulları", tid: "footer-legal-terms" },
  ];
  return (
    <footer id="iletisim" className="scroll-mt-20 text-white" style={{ background: NAVY }} data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <Wordmark dark size="h-8" />
          <p className="mt-4 text-sm leading-relaxed text-slate-400 max-w-xs">
            {t("İşletmelerin günlük ve gelecekteki para akışını daha kolay takip etmesini sağlayan finans yönetim platformu.")}
          </p>
          <div className="mt-5"><LanguageToggle dark /></div>
        </div>
        <nav aria-label={t("Menü")}>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500">{t("Menü")}</p>
          <ul className="mt-4 space-y-2.5">
            {links.map((l) => (
              <li key={l.tid}>
                <button type="button" onClick={() => scrollTo(l.id)} data-testid={l.tid}
                  className="text-sm text-slate-300 hover:text-white transition-colors duration-150">
                  {t(l.label)}
                </button>
              </li>
            ))}
            <li>
              <Link to="/login" data-testid="footer-link-login"
                className="text-sm text-slate-300 hover:text-white transition-colors duration-150">{t("Giriş Yap")}</Link>
            </li>
            <li>
              <Link to="/register" data-testid="footer-link-register"
                className="text-sm text-slate-300 hover:text-white transition-colors duration-150">{t("Firma Kaydı")}</Link>
            </li>
            <li>
              <button type="button" onClick={() => scrollTo("iletisim")} data-testid="footer-link-contact"
                className="text-sm text-slate-300 hover:text-white transition-colors duration-150">{t("İletişim")}</button>
            </li>
          </ul>
        </nav>
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500">{t("İletişim")}</p>
          <p className="mt-4 text-sm text-slate-400" data-testid="footer-contact-note">
            {t("İletişim kanallarımız çok yakında burada yer alacak.")}
          </p>
          <p className="mt-6 text-xs font-semibold tracking-[0.18em] uppercase text-slate-500">{t("Yasal")}</p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            {legal.map((l) => (
              <span key={l.tid} data-testid={l.tid} className="text-sm text-slate-500">
                {t(l.label)} <span className="text-[10px] text-slate-600">({t("Yakında")})</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 text-xs text-slate-500">
          {t("© 2026 TEMHA — Çok firmalı finans yönetim platformu")}
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useLang();

  if (user) return <Navigate to="/panel" replace />;

  return (
    <div id="top" className="min-h-screen bg-white text-slate-700 overflow-x-clip" data-testid="landing-page">
      <LandingHeader />

      {/* HERO */}
      <section id="urun" className="scroll-mt-20 border-b border-slate-100" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{t("Finans Yönetim Platformu")}</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.08] text-[#0A1128]">
              {t("Paranızın bugününü görün,")}{" "}
              <span style={{ color: BLUE }}>{t("yarınını planlayın.")}</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-slate-500 max-w-xl">
              {t("TEMHA, işletmenizin günlük ve gelecekteki para akışını daha kolay takip etmenizi sağlayan finans yönetim platformudur.")}
            </p>
            <p className="mt-3 text-sm lg:text-base leading-relaxed text-slate-500 max-w-xl">
              {t("Çeklerinizi, ödemelerinizi, tahsilatlarınızı, kasanızı, kredi kartlarınızı, POS hareketlerinizi, DBS işlemlerinizi ve aboneliklerinizi tek merkezden yönetin.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => scrollTo("ozellikler")} data-testid="landing-cta-explore"
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 transition-all duration-200 hover:-translate-y-[1px]">
                {t("Ürünü Keşfet")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 px-7">
                <Link to="/login" data-testid="landing-cta-login">{t("Giriş Yap")}</Link>
              </Button>
            </div>
          </div>
          <div className="fade-up">
            <BrowserFrame testid="hero-product-preview">
              <DashboardMock />
            </BrowserFrame>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-20 lg:py-24" data-testid="landing-values">
        <div className="max-w-6xl mx-auto px-6">
          <ValueCards />
        </div>
      </section>

      {/* FEATURE GROUPS */}
      <section id="ozellikler" className="scroll-mt-20 py-20 lg:py-28 bg-slate-50 border-y border-slate-100" data-testid="landing-features">
        <div className="max-w-6xl mx-auto px-6 space-y-14">
          <SectionHeading
            title="Finansal hareketlerinizi tek yerde yönetin."
            text="TEMHA, işletmenizin günlük para giriş ve çıkışlarını takip etmenizi kolaylaştıran araçları tek bir platformda bir araya getirir."
          />
          <FeatureGroups />
        </div>
      </section>

      {/* SHOWCASES */}
      <section className="py-20 lg:py-28" data-testid="landing-showcase">
        <div className="max-w-6xl mx-auto px-6 space-y-24">
          <Showcase title="Ödemelerinizi ve tahsilatlarınızı aynı sistemde takip edin."
            text="Planlanan ve gerçekleşen ödemelerinizi ve tahsilatlarınızı tek platformda düzenli şekilde takip edin."
            testid="showcase-payments">
            <BrowserFrame url="temha.app/odemeler"><PaymentsCollectionsMock /></BrowserFrame>
          </Showcase>
          <Showcase flip title="Çek hareketlerinizi kontrol altında tutun."
            text="Alınan, bankaya verilen, cirolanan ve kendi çeklerinizi merkezi olarak yönetin."
            testid="showcase-checks">
            <BrowserFrame url="temha.app/cekler/alinan"><ChecksMock /></BrowserFrame>
          </Showcase>
          <Showcase title="Kasa, POS ve kart hareketlerinizi görün."
            text="Nakit giriş-çıkışlarınızı, POS günsonu hareketlerinizi ve kart ekstrelerinizi tek yerden takip edin."
            testid="showcase-cash">
            <BrowserFrame url="temha.app/kasa"><CashPosCardMock /></BrowserFrame>
          </Showcase>
          <Showcase flip title="Finansal görünümünüzü tek panelden takip edin."
            text="Yaklaşan ödemelerinizi, tahsilatlarınızı ve güncel TCMB kurlarını tek panelde görün."
            testid="showcase-dashboard">
            <BrowserFrame url="temha.app/panel"><DashboardMock /></BrowserFrame>
          </Showcase>
        </div>
      </section>

      {/* DAILY MONEY FLOW */}
      <section className="py-20 lg:py-28 bg-slate-50 border-y border-slate-100" data-testid="landing-money-flow">
        <div className="max-w-6xl mx-auto px-6 space-y-14">
          <SectionHeading
            title="Bugün ne kadar para girdi, ne kadar çıktı?"
            text="TEMHA, işletmenizin günlük finansal hareketlerini tek bir yerde takip etmenize yardımcı olur."
          />
          <MoneyFlow />
        </div>
      </section>

      {/* FUTURE PLANNING */}
      <section className="py-20 lg:py-28 text-white" style={{ background: NAVY }} data-testid="landing-planning">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <SectionHeading center={false} dark
            kicker="Yarını Planlayın"
            title="Yarın ne olacak?"
            text="Yaklaşan ödeme ve tahsilatlarınızı daha düzenli takip ederek finansal planlamanızı kolaylaştırın."
          />
          <div className="flex flex-wrap gap-2.5 lg:justify-end">
            {["Yaklaşan Ödemeler", "Yaklaşan Tahsilatlar", "Alınan Çekler", "Kendi Çeklerimiz", "Abonelikler", "POS Hareketleri", "DBS"].map((m) => (
              <span key={m} className="text-xs font-medium px-3 py-1.5 rounded-md border border-white/15 bg-white/5 text-slate-200">{t(m)}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="nasil-calisir" className="scroll-mt-20 py-20 lg:py-28" data-testid="landing-how-it-works">
        <div className="max-w-6xl mx-auto px-6 space-y-14">
          <SectionHeading title="Nasıl Çalışır?" />
          <HowItWorks />
        </div>
      </section>

      {/* WHY TEMHA */}
      <section id="hakkimizda" className="scroll-mt-20 py-20 lg:py-28 text-white" style={{ background: NAVY }} data-testid="landing-why">
        <div className="max-w-6xl mx-auto px-6 space-y-14">
          <SectionHeading dark title="Finans yönetimini daha anlaşılır hale getirin." />
          <WhyTemha />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28" data-testid="landing-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1128]">
            {t("Finansal hareketlerinizi tek yerde görmek ister misiniz?")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-500 max-w-xl mx-auto">
            {t("TEMHA ile işletmenizin günlük ve gelecekteki para akışını daha kolay takip edin.")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => scrollTo("ozellikler")} data-testid="landing-cta-view"
              className="bg-blue-600 hover:bg-blue-700 text-white px-7 transition-all duration-200 hover:-translate-y-[1px]">
              {t("Ürünü İncele")}
            </Button>
            <Button asChild size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 px-7">
              <Link to="/login" data-testid="landing-cta-login-2">{t("Giriş Yap")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* REGISTER CTA */}
      <section className="pb-24" data-testid="landing-register-cta">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl border border-blue-100 bg-blue-50/60 px-8 py-14 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0A1128]">
              {t("Firmanızı TEMHA ile yönetmeye başlayın.")}
            </h2>
            <div className="mt-7">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 transition-all duration-200 hover:-translate-y-[1px]">
                <Link to="/register" data-testid="landing-register-button">{t("Firma Kaydı Oluştur")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
